import React from 'react';
import { EyeIcon } from '@heroicons/react/24/outline';
import type { PhoneHealthListItem } from '../../../services/phoneNumberHealthService';
import { HealthStatusBadge, UsageTypeBadge, CarrierIssueBadge } from './StatusBadges';
import UtilizationBar from './UtilizationBar';
import { Pagination } from '../../../components/UI/Table';
import TelnyxBadge from './TelnyxBadge';
import PhoneNumbersSection from './PhoneNumbersSection';

const formatRelative = (d: string | null) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(d).toLocaleDateString();
};

const HEALTH_OPTIONS = [
  { value: 'healthy', label: 'Healthy' },
  { value: 'warning', label: 'Warning' },
  { value: 'replace', label: 'Replace' },
  { value: 'warm_up', label: 'Warm-Up' },
  { value: 'quarantine', label: 'Quarantine' },
  { value: 'retired', label: 'Retired' },
  { value: 'out_of_inventory', label: 'Out of Inventory' },
];

interface Props {
  items: PhoneHealthListItem[];
  page: number;
  pages: number;
  total: number;
  loading?: boolean;
  periodLabel?: string;
  onPageChange: (p: number) => void;
  onViewDetails: (item: PhoneHealthListItem) => void;
  onStatusChange: (id: string, status: string) => void;
}

const PhoneNumberTable: React.FC<Props> = ({
  items,
  page,
  pages,
  total,
  loading,
  periodLabel = 'Today',
  onPageChange,
  onViewDetails,
  onStatusChange,
}) => (
  <PhoneNumbersSection
    title="All Phone Numbers"
    description="Click a number for full metrics. Change health status inline."
    noPadding
  >
    {items.length === 0 && !loading ? (
      <p className="text-sm text-muted-foreground text-center py-14 px-4">
        No numbers match your filters.
      </p>
    ) : (
      <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-4 py-3 font-medium">Number</th>
            <th className="px-4 py-3 font-medium">Area / TZ</th>
            <th className="px-4 py-3 font-medium whitespace-nowrap">Usage</th>
            <th className="px-4 py-3 font-medium">Health</th>
            <th className="px-4 py-3 font-medium">{periodLabel}</th>
            <th className="px-4 py-3 font-medium">Connected</th>
            <th className="px-4 py-3 font-medium">Text</th>
            <th className="px-4 py-3 font-medium">Warm-Up Day</th>
            <th className="px-4 py-3 font-medium">Carrier</th>
            <th className="px-4 py-3 font-medium">Last Used</th>
            <th className="px-4 py-3 font-medium" />
          </tr>
        </thead>
        <tbody>
          {items.map((row, idx) => (
            <tr key={row.id} className={`border-t border-border/60 hover:bg-muted/25 transition-colors ${idx % 2 === 1 ? 'bg-muted/10' : ''}`}>
              <td className="px-4 py-3">
                <button
                  type="button"
                  onClick={() => onViewDetails(row)}
                  className="font-mono text-left font-medium hover:text-primary hover:underline block"
                >
                  {row.phoneNumber}
                </button>
                <div className="mt-1"><TelnyxBadge /></div>
              </td>
              <td className="px-4 py-3">
                <div>{row.areaCode ?? '—'}</div>
                <div className="text-xs text-muted-foreground">{row.timezone ?? 'Unknown'}</div>
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap"><UsageTypeBadge label={row.purposeLabel} /></td>
              <td className="px-3 py-2.5">
                <select
                  value={row.healthStatus}
                  onChange={(e) => onStatusChange(row.id, e.target.value)}
                  className="text-xs border rounded-md px-1.5 py-1 bg-background max-w-[110px]"
                >
                  {HEALTH_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {row.healthStatusSince && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Since {new Date(row.healthStatusSince).toLocaleDateString()}
                  </p>
                )}
                {row.healthStatusManual && (
                  <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-0.5 font-medium">
                    Manual
                    {row.healthManualCallsRemaining != null
                      ? ` — ${row.healthManualCallsRemaining} calls to unlock`
                      : ' override'}
                  </p>
                )}
              </td>
              <td className="px-3 py-2.5">
                <UtilizationBar
                  used={row.callsToday}
                  capacity={row.effectiveDailyCallLimit}
                  percent={row.utilizationTodayPercent}
                />
              </td>
              <td className="px-3 py-2.5 text-xs">
                {row.connectRate != null ? `${(row.connectRate * 100).toFixed(1)}%` : '—'}
                <div className="text-muted-foreground">{row.callsAnswered}/{row.callsMade}</div>
              </td>
              <td className="px-3 py-2.5 text-xs">
                {row.deliveryRate != null ? `${(row.deliveryRate * 100).toFixed(1)}%` : '—'}
                <div className="text-muted-foreground">{row.textsDelivered}/{row.textsSent}</div>
              </td>
              <td className="px-3 py-2.5 text-xs">
                {row.healthStatus === 'warm_up' ? `Day ${row.warmupDay}` : '—'}
              </td>
              <td className="px-3 py-2.5"><CarrierIssueBadge issues={row.carrierIssues} /></td>
              <td className="px-3 py-2.5 text-xs">{formatRelative(row.lastUsedAt)}</td>
              <td className="px-4 py-3">
                <button
                  type="button"
                  onClick={() => onViewDetails(row)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <EyeIcon className="h-3.5 w-3.5" />
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    )}
    <div className="px-4 py-3 border-t border-border/60 flex items-center justify-between text-sm text-muted-foreground bg-muted/20">
      <span>Page {page} of {pages} · {total} total</span>
      <Pagination
        currentPage={page}
        totalPages={pages}
        totalItems={total}
        itemsPerPage={25}
        onPageChange={onPageChange}
      />
    </div>
  </PhoneNumbersSection>
);

export default PhoneNumberTable;
